/**
 * 프리셋 — "이 이미지는 어떤 물건인가"에 대한 답 묶음.
 *
 * 슬라이더 15개를 사람에게 던지는 건 설계 실패다. 프리셋이 먼저 그럴듯한 지점을
 * 잡아주고, 사람은 거기서 손을 대면 된다. `auto`는 이미지 통계를 보고 고른다.
 */

import type { ForgeParams, ImageStats, Preset, PresetId } from './types.ts';

export const DEFAULT_PARAMS: ForgeParams = {
  preset: 'auto',
  resolution: 128,
  meshMode: 'inflate',
  depthScale: 0.35,
  inflate: 0.28,
  smooth: 2,
  normalStrength: 1.0,
  delight: 0.6,
  roughnessBias: 0,
  metalnessBias: 0,
  aoStrength: 0.7,
  emissiveStrength: 0,
  toonSteps: 4,
  toonMix: 0,
  rimStrength: 0.25,
  rimColor: '#7fd4ff',
  iridescence: 0,
  backTint: 0.35,
  hullResolution: 224,
  hullSmooth: 1,
  sideFacesLeft: true,
  hullRelief: 0.02,
  hullCarve: 0.15,
  bakeTexture: true,
  targetTriangles: 30000,
  hullTolerance: 0,
  hullDilate: 0.004,
  hullDepthRelief: 0.03,
};

export const PRESETS: Preset[] = [
  {
    id: 'auto',
    label: '자동',
    hint: '이미지 통계를 보고 아래 프리셋 중 하나를 고릅니다.',
    params: {},
  },
  {
    id: 'monster',
    label: '몬스터',
    hint: '거친 피부, 깊은 골, 강한 요철. 유기적인 실루엣에.',
    params: {
      depthScale: 0.42,
      inflate: 0.34,
      normalStrength: 1.4,
      roughnessBias: 0.12,
      aoStrength: 0.9,
      toonMix: 0.15,
      rimStrength: 0.35,
      rimColor: '#8bff9a',
    },
  },
  {
    id: 'character',
    label: '캐릭터',
    hint: '피부와 천. 디라이팅을 세게, 요철은 부드럽게, 살짝 툰.',
    params: {
      depthScale: 0.34,
      inflate: 0.3,
      normalStrength: 0.7,
      delight: 0.75,
      roughnessBias: 0.05,
      aoStrength: 0.5,
      toonSteps: 4,
      toonMix: 0.5,
      rimStrength: 0.4,
      rimColor: '#ffd9a8',
    },
  },
  {
    id: 'metal',
    label: '금속',
    hint: '메탈니스를 올리고 러프니스를 내린다. 갑옷, 무기, 로봇.',
    params: {
      depthScale: 0.3,
      inflate: 0.24,
      normalStrength: 1.2,
      delight: 0.4,
      roughnessBias: -0.3,
      metalnessBias: 0.55,
      aoStrength: 0.6,
      rimStrength: 0.15,
      rimColor: '#cfe6ff',
    },
  },
  {
    id: 'slime',
    label: '슬라임',
    hint: '반들거리고 두껍게 부푼다. 이리데슨스와 은은한 발광.',
    params: {
      depthScale: 0.3,
      inflate: 0.5,
      smooth: 4,
      normalStrength: 0.5,
      roughnessBias: -0.45,
      metalnessBias: 0.05,
      aoStrength: 0.4,
      emissiveStrength: 0.25,
      iridescence: 0.8,
      rimStrength: 0.6,
      rimColor: '#a8ffef',
    },
  },
  {
    id: 'stone',
    label: '돌·콘크리트',
    hint: '요철 최대, AO 최대, 반사 없음.',
    params: {
      depthScale: 0.38,
      inflate: 0.26,
      normalStrength: 1.6,
      roughnessBias: 0.25,
      aoStrength: 1.0,
      rimStrength: 0.1,
      rimColor: '#b8b0a4',
    },
  },
  {
    id: 'crystal',
    label: '크리스탈',
    hint: '날카로운 면, 강한 림, 안쪽에서 빛이 새어 나오는 느낌.',
    params: {
      depthScale: 0.46,
      inflate: 0.42,
      smooth: 0,
      normalStrength: 1.1,
      roughnessBias: -0.5,
      metalnessBias: 0.15,
      emissiveStrength: 0.35,
      iridescence: 0.5,
      rimStrength: 0.8,
      rimColor: '#c9b6ff',
    },
  },
  {
    id: 'flat',
    label: '플랫 일러스트',
    hint: '2D 원화의 색을 그대로 살린다. 요철 최소, 툰 최대.',
    params: {
      depthScale: 0.26,
      inflate: 0.24,
      normalStrength: 0.3,
      delight: 0.9,
      roughnessBias: 0.3,
      aoStrength: 0.3,
      toonSteps: 3,
      toonMix: 0.85,
      rimStrength: 0.3,
      rimColor: '#ffffff',
    },
  },
];

const BY_ID = new Map(PRESETS.map((p) => [p.id, p]));

/**
 * 통계로 프리셋 추측하기. 정답은 없고, 사람이 두 번 클릭할 걸 한 번으로 줄이는 게 목적이다.
 * 순서가 곧 우선순위다.
 */
export function suggestPreset(stats: ImageStats): PresetId {
  const { meanLuma: luma, meanSaturation: sat, specularRatio: spec, detail, contrast, shadowRatio } = stats;

  // 문턱값은 src/assets 갤러리 10장의 실측치로 잡았다 (README "프리셋" 표 참고).
  //   흰 털복숭이  luma .53 sat .11 spec .23 det .048
  //   얼음 망령    luma .61 sat .23 spec .25 det .124
  //   기사/로봇    luma .2  sat .36 spec .02~.05 shadow .3~.44
  //   슬라임       luma .40 sat .75 det .048
  //   골렘         luma .14 sat .54 det .066 shadow .51

  // 밝고 하이라이트가 많은 무채색 = 흰 털이거나 얼음. 금속으로 찍으면 새까매진다.
  if (luma > 0.45 && spec > 0.12 && sat < 0.3) return detail > 0.1 ? 'crystal' : 'character';
  // 채도 낮고 그림자가 깊고 하이라이트가 있는 것 = 금속.
  if (sat < 0.42 && spec > 0.015 && shadowRatio > 0.2) return 'metal';
  // 채도 높고 매끈하고 밝은 것 = 젤리.
  if (sat > 0.65 && detail < 0.06 && luma > 0.3) return 'slime';
  // 어둡고 채도 있고 거친 것 = 바위·나무껍질.
  if (shadowRatio > 0.45 && detail < 0.08) return 'stone';
  if (detail > 0.06 && contrast > 0.15) return 'monster';
  if (sat > 0.5 && detail < 0.03) return 'flat';
  return 'character';
}

/**
 * 턴어라운드(다중 뷰) 입력일 때 프리셋 위에 덧씌우는 값.
 *
 * 프리셋의 색 림라이트(몬스터 = 연두, 캐릭터 = 살구)는 단일 스프라이트를 "게임 렌더처럼"
 * 보이게 하는 장치였는데, 여덟 장을 정성껏 뽑은 캐릭터에서는 원색을 물들이는 버그로
 * 보였다(실측: 초록 골렘, 갈색 로봇). 생성 이미지는 이미 균일 조명이라 디라이팅도
 * 세게 걸 이유가 없다. 원본 색이 그대로 보이는 것이 하이엔드의 첫 조건이다.
 */
const TURNAROUND_TWEAKS: Partial<ForgeParams> = {
  rimStrength: 0.06,
  rimColor: '#ffffff',
  toonMix: 0,
  iridescence: 0,
  emissiveStrength: 0,
};

/** 프리셋을 기본값 위에 얹는다. `auto`는 통계로 실제 프리셋을 먼저 결정한다. */
export function resolveParams(
  preset: PresetId,
  stats: ImageStats | null,
  overrides: Partial<ForgeParams> = {},
  turnaround = false,
): { params: ForgeParams; effective: PresetId } {
  const effective: PresetId = preset === 'auto' ? (stats ? suggestPreset(stats) : 'character') : preset;
  const base = BY_ID.get(effective)?.params ?? {};
  const merged: ForgeParams = { ...DEFAULT_PARAMS, ...base, preset };
  if (turnaround) {
    Object.assign(merged, TURNAROUND_TWEAKS);
    merged.delight = Math.min(merged.delight, 0.35);
    merged.normalStrength = Math.min(merged.normalStrength, 0.9);
  }
  return {
    params: { ...merged, ...overrides, ...urlOverrides(), preset },
    effective,
  };
}

/**
 * URL 쿼리로 파라미터를 덮어쓴다 — 품질 루프용. `?p.hullResolution=224&p.hullTolerance=0&p.hullSmooth=1`
 * 숫자·불리언만. 헤드리스 실측이 슬라이더를 더듬지 않고 같은 조건을 재현하려는 장치.
 */
function urlOverrides(): Partial<ForgeParams> {
  if (typeof location === 'undefined') return {};
  const out: Record<string, number | boolean> = {};
  for (const [k, v] of new URLSearchParams(location.search)) {
    if (!k.startsWith('p.')) continue;
    const key = k.slice(2);
    if (!(key in DEFAULT_PARAMS)) continue;
    out[key] = v === 'true' ? true : v === 'false' ? false : Number(v);
  }
  return out as Partial<ForgeParams>;
}

export function presetById(id: PresetId): Preset | undefined {
  return BY_ID.get(id);
}
